/*
 * patchistry-builder.js — build 2026-05-15T20:40Z
 * MICRO-INTERACTIONS 2026-06-25f
 * BRYGUY-CHIP 2026-07-14 — labeled + imaged the Story Time With BryGuy filter chip.
 * Cache-bust marker — every push appends to this header to force v= regeneration.
 */
(() => {
  'use strict';

  const VALID_VARIANT_ID = /^[1-9]\d*$/;

  function configFor(root) {
    const mode = root?.dataset?.pcrBuilderMode === 'badge-reel' ? 'badge-reel' : 'build-yours';
    const noun = String(root?.dataset?.pcrCanvasNoun || (mode === 'badge-reel' ? 'Reel' : 'Hat'));
    const plural = String(root?.dataset?.pcrCanvasNounPlural || (mode === 'badge-reel' ? 'Reels' : 'Hats'));
    if (mode === 'badge-reel') {
      return Object.freeze({
        mode,
        noun,
        plural,
        emptyName: 'No reel yet',
        trayEmptyName: 'No reel yet',
        emptyInstruction: 'Choose your reel to start',
        patchOnlyName: 'Patch-only build',
        trayPatchOnlyName: 'Patch-only build',
        patchOnlyInstruction: 'Choose your first badge patch',
        selectedName: (name) => `${name} selected`,
        chooseFirst: 'Choose Reel First',
        baseOnlyAction: 'Add Reel To Cart',
        buildAction: 'Add Build To Cart',
        emptyCount: 'Choose your reel to start',
        resetValue: 'Choose Reel First',
        selectionLabel: 'Your Reel Selection:',
        lockedPatchCopy: (total) => `${total} BADGE PATCHES WAITING — CHOOSE YOUR REEL TO UNLOCK`,
        stepSummaryInitial: 'Choose Reel First',
        stepSummaryInitialSub: 'Choose your reel to start',
        stepEmptySub: 'Choose your reel to start',
        stepSelectedSub: 'Reel selected. Tap Next to unlock badge patches.',
        patchOnlySub: 'Choose your first badge patch',
        readySub: 'Your reel is ready. Build your signal.',
        patchOnlyEmptyAction: 'Choose your first badge patch',
        patchOnlyAction: 'Add Build To Cart',
        manifestoHeading: 'More than a reel. More than a badge patch.',
        manifestoBody: 'Patchistry is built for identity — choose the reel and badge patches that say what matters without saying a word.',
      });
    }
    return Object.freeze({
      mode,
      noun,
      plural,
      emptyName: 'No hat yet',
      trayEmptyName: 'No hat yet',
      emptyInstruction: 'Pick a hat to start',
      patchOnlyName: 'Patch Pack',
      trayPatchOnlyName: 'Patch pack',
      patchOnlyInstruction: 'Choose your first patch',
      selectedName: (name) => name,
      chooseFirst: 'Choose Canvas First',
      baseOnlyAction: 'Add Hat To Cart',
      buildAction: 'Add Build To Cart',
      emptyCount: 'Pick a hat or patch to start',
      resetValue: 'Choose Your Canvas',
      selectionLabel: 'Your Hat Selection:',
      lockedPatchCopy: (total) => `${total} PATCHES WAITING — PICK YOUR CANVAS TO UNLOCK`,
      stepSummaryInitial: 'Canvas Selected',
      stepSummaryInitialSub: 'Your patch wall is unlocked.',
      stepEmptySub: 'Select one option to unlock Step 2.',
      stepSelectedSub: 'Canvas selected. Tap Next to unlock patches.',
      patchOnlySub: 'Patch Pack mode unlocked.',
      readySub: 'Your canvas is ready. Build your signal.',
      patchOnlyEmptyAction: 'Choose Patches',
      patchOnlyAction: 'Add Patch Pack To Cart',
      manifestoHeading: 'More than a hat. More than a patch.',
      manifestoBody: 'Patchistry is a canvas for identity — designed to help you express who you are, how you feel, and what you want to say without saying a word.',
    });
  }

  function parseVariantIds(values, allowedIds, limit = Number.POSITIVE_INFINITY) {
    const allowed = new Set(Array.from(allowedIds || [], String));
    const unique = [];
    const seen = new Set();
    const rawValues = Array.isArray(values) ? values : [values];
    for (const raw of rawValues) {
      for (const token of String(raw || '').split(',')) {
        const id = token.trim();
        if (!VALID_VARIANT_ID.test(id) || !allowed.has(id) || seen.has(id)) continue;
        seen.add(id);
        unique.push(id);
        if (unique.length >= limit) return unique;
      }
    }
    return unique;
  }

  function cardIds(root, selector) {
    return Array.from(root?.querySelectorAll?.(selector) || [])
      .map((card) => String(card?.dataset?.variantId || ''))
      .filter((id) => VALID_VARIANT_ID.test(id));
  }

  function preselectionFor(search, root) {
    const config = configFor(root);
    const params = new URLSearchParams(String(search || ''));
    const canvasKey = config.mode === 'badge-reel' ? 'reel' : 'canvas';
    const allCanvasIds = parseVariantIds(
      params.getAll(canvasKey),
      cardIds(root, '[data-pcr-canvas-card]'),
    );
    const canvasIds = config.mode === 'badge-reel' && allCanvasIds.length !== 1
      ? []
      : allCanvasIds;
    const patchIds = parseVariantIds(
      params.getAll('patches'),
      cardIds(root, '[data-pcr-card]'),
    );
    if (config.mode === 'badge-reel' && params.getAll(canvasKey).join(',').includes(',') && !canvasIds.length) {
      return { canvasIds: [], patchIds: [] };
    }
    return { canvasIds, patchIds };
  }

  function ajaxCartRoot() {
    const configured = String(window.Shopify?.routes?.root || '/');
    const path = configured.replace(/^\/+|\/+$/g, '');
    return path ? `/${path}/` : '/';
  }

  function cartJsonUrl() {
    return `${ajaxCartRoot()}cart.js`;
  }

  function cartAddUrl() {
    return window.routes?.cart_add_url || `${ajaxCartRoot()}cart/add.js`;
  }

  function cartChangeUrl() {
    return window.routes?.cart_change_url || `${ajaxCartRoot()}cart/change.js`;
  }

  window.PatchistryBuilderMode = Object.freeze({
    configFor,
    parseVariantIds,
    preselectionFor,
    cartJsonUrl,
    cartAddUrl,
    cartChangeUrl,
  });
})();
(() => {
  'use strict';

  const ROOT = '[id^="PatchistryCameraRoll-"]';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const productCache = new Map();
  const MERCHANDISING_PAGE_SEED =
    Math.floor(Math.random() * 0x100000000) >>> 0;

  // ── THE FOUR-BAND CONTRACT ────────────────────────────────────────────────
  //
  // Brian, 2026-07-28, verbatim:
  //   "Top 3, rotating of top news, rotating of top candyz, the full random
  //    shuffle after. Make this be solely analytics based and research backed."
  //
  // Refines the earlier contract, which still holds where it doesn't conflict:
  //   "storytimewithbryguy lower except for the blow me patch can be higher up,
  //    but everything else should be constantly shuffled for rediscovery"
  //
  //   band 1  anchors      3 pinned, fixed        <- proven by order frequency
  //   band 2  new drop     8 rotating             <- recency, NOT sales
  //                                                  (priority tags lead it)
  //   band 3  candyz       6 rotating             <- volume tag, unranked within
  //   band 4  everything   one true random shuffle
  //           storytime    demoted block (blow-me exempt, rides band 4)
  //           unavailable  last
  //
  // ── THE EVIDENCE (all 11 paid orders, re-pulled 2026-07-28) ───────────────
  //
  // Counted by ORDER FREQUENCY — how many distinct orders contain the patch —
  // NOT by units. Units lie here: order #1012 was one customer buying Throat
  // Goat x3, which reads as "3 sold" but is a single person's taste. A pin's
  // job is to appeal to the NEXT stranger, so breadth is the honest metric.
  //
  //   6/11 (55%)  liquor-where-she-likes-it   <- the anchor. Nothing is close.
  //   3/11 (27%)  strong-as-a-mother
  //   2/11 (18%)  i-come-with-baggage, can-i-pet-that-dog, beaver,
  //               ray-of-fing-sunshine, fire, lips
  //   1/11        everything else, incl. throat-goat
  //
  // Throat Goat was pinned #1 and is demoted here: 1 order out of 11.
  // #3 is a five-way tie at 2 orders, broken by recency — i-come-with-baggage
  // is the only one whose orders both land in the most recent fortnight.
  //
  // HONESTY NOTE: only band 1 slot #1 is statistically solid. At n=11, a patch
  // seen twice is not meaningfully ahead of one seen once. Slots #2/#3 are
  // directional. Re-pull after every drop and let the order data move them.
  const MERCHANDISING_POLICY = Object.freeze({
    observedAt: '2026-07-28T06:00:00.000Z',
    expiresAt: '2026-08-11T06:00:00.000Z',
    // TOP 3 ONLY. Adding a fourth breaks the contract above.
    winnerPaths: Object.freeze([
      '/products/liquor-where-she-likes-it',
      '/products/strong-as-a-mother',
      '/products/i-come-with-baggage'
    ]),
    // Band 2 has NO sales evidence by definition — a patch that shipped this
    // week cannot have an order history. So it is ordered by recency, capped,
    // and rotated. Patches earn their way into band 1 by order frequency only.
    // Two drops' worth at the 15-a-week cadence. Wider just dilutes "new".
    newDropWindowDays: 14,
    // 2026-07-28, Brian: "push some of the new patches right below the best
    // sellers ... especially the pickleball patches".
    //
    // The 14-day window alone could not deliver that. It caught BOTH the
    // 2026-07-15 batch (21 patches) and the 2026-07-28 batch (19), so 40
    // patches competed for 6 slots and the newest drop got about half of them.
    // freshDropWindowDays carves out the newest cohort and gives it first claim
    // on the band; the 14-day window still backfills, so the band never empties
    // if a weekly drop slips.
    freshDropWindowDays: 7,
    // Tags that lead the new band. ⚠️ THIS IS A BET, NOT EVIDENCE: Pickleball
    // has ZERO order history (see composition-intelligence) — every paid order
    // to date skews Candyz / No-Filter / Wild-Card. It is promoted because it
    // is the creator-seeding target and Brian's read on where demand is going.
    // Re-check against real orders before renewing it.
    newDropPriorityTags: Object.freeze(['pickleball']),
    // Band 2 runs wider than the others so the priority tags do not crowd out
    // the rest of the drop: 4 pickleball lead, 4 slots still rotate across the
    // other fresh patches.
    newDropBandSize: 8,
    // Band 3. Candyz is the #1 tag by units sold (12, ahead of No-Filter's 10)
    // and at $5 it is the filler that walks a basket from 3 patches to 8 —
    // which matters because 3+ patch orders are 7/11 of orders but 79% of
    // revenue. That justifies lifting Candyz OUT of the old bottom lane.
    //
    // It does NOT justify ranking within it. Those 12 units spread across 10
    // distinct Candyz, so the expected count per patch is ~1.2 and the best
    // performers (fire, lips) sit at 2. That is Poisson noise, not a ranking.
    // Empty on purpose: seeds go here the moment a Candyz genuinely separates.
    candyzSeedPaths: Object.freeze([]),
    // How many items each rotating band puts above the shuffle. Uncapped, the
    // bands stop being bands: 19 new + 60 Candyz would push the other ~90
    // patches past position 80 on every load, which is the opposite of the
    // rediscovery the shuffle exists for. Capped, every new and Candyz patch
    // gets a turn in the prime slot across visits.
    bandSize: 6,
    // Exempt from the bryguy demotion — rides the shuffle, not pinned.
    demotionExemptPaths: Object.freeze([
      '/products/blow-me'
    ]),
    demotedTags: Object.freeze([
      'storytimewithbryguy'
    ])
  });

  // Set by rankProductRecords on each run; surfaced on the window export so a
  // dashboard check can tell when the demand snapshot needs re-pulling.
  let lastSnapshotWasCurrent = null;

  function normalizedProductPath(value) {
    try {
      const url = new URL(String(value || ''), 'https://patchistry.com');
      return url.pathname.replace(/\/+$/, '') || '/';
    } catch (_) {
      return String(value || '').split('?')[0].replace(/\/+$/, '');
    }
  }

  function shuffledProductRecords(records, random = Math.random) {
    const shuffled = [...records];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const roll = Number(random());
      const normalizedRoll = Number.isFinite(roll)
        ? Math.min(Math.max(roll, 0), 0.999999999)
        : 0.5;
      const swapIndex = Math.floor(normalizedRoll * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    return shuffled;
  }

  function seededProductRandom(seed = MERCHANDISING_PAGE_SEED) {
    let state = Number(seed) >>> 0;
    return () => {
      state = (state + 0x6D2B79F5) >>> 0;
      let value = state;
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 0x100000000;
    };
  }

  function recordTags(record) {
    const raw = Array.isArray(record.tags)
      ? record.tags
      : String(record.tags || '').split(/[\s,]+/);
    return new Set(raw.map(normalizeTag).filter(Boolean));
  }

  function recordHasAnyTag(record, expectedTags) {
    const tags = recordTags(record);
    return expectedTags.some((tag) => tags.has(tag));
  }

  function recordIsCandyz(record) {
    return (
      String(record.collection || '').toLowerCase().includes('candyz') ||
      recordTags(record).has('candyz')
    );
  }

  function recordIsDemotionExempt(record) {
    return MERCHANDISING_POLICY.demotionExemptPaths
      .indexOf(normalizedProductPath(record.path)) !== -1;
  }

  function recordIsStorytime(record) {
    // blow-me carries the bryguy tag but is explicitly exempt: Brian wants it
    // ranking with the normal patches, not buried with the rest of the set.
    if (recordIsDemotionExempt(record)) return false;
    return recordHasAnyTag(record, MERCHANDISING_POLICY.demotedTags);
  }

  // Band 2's membership test. `created` is the Unix seconds Liquid stamps onto
  // each card as data-created, so the band maintains itself: every weekly drop
  // enters automatically and ages out on its own. No hand-edited handle list.
  function recordAgeDays(record, now) {
    const created = Number(record.created || 0);
    if (!Number.isFinite(created) || created <= 0) return null;
    const age = (Number(now) / 1000 - created) / 86400;
    return age >= 0 ? age : null;
  }

  function recordIsNewDrop(record, now) {
    const age = recordAgeDays(record, now);
    return age !== null && age <= MERCHANDISING_POLICY.newDropWindowDays;
  }

  // The newest cohort — gets first claim on band 2 so the latest drop is not
  // diluted by the previous one.
  function recordIsFreshDrop(record, now) {
    const age = recordAgeDays(record, now);
    return age !== null && age <= MERCHANDISING_POLICY.freshDropWindowDays;
  }

  // Band 2 ordering: fresh + priority tag, then the rest of the fresh cohort,
  // then older-but-still-new. Every group is shuffled internally, so even the
  // pinned-forward pickleball patches change order between visits.
  function orderedNewDrop(records, now, random) {
    const priority = [];
    const fresh = [];
    const older = [];
    records.forEach((record) => {
      if (!recordIsFreshDrop(record, now)) { older.push(record); return; }
      if (recordHasAnyTag(record, MERCHANDISING_POLICY.newDropPriorityTags)) priority.push(record);
      else fresh.push(record);
    });
    return [
      ...shuffledProductRecords(priority, random),
      ...shuffledProductRecords(fresh, random),
      ...shuffledProductRecords(older, random),
    ];
  }

  // Puts any evidence-backed seeds at the front of a band in the order given,
  // then shuffles the remainder. With no seeds — which is the honest state of
  // the Candyz data today — this degrades to a plain shuffle.
  function seededFirst(records, seedPaths, random) {
    const seedRank = new Map(
      Array.from(seedPaths || []).map((path, index) => [path, index])
    );
    if (!seedRank.size) return shuffledProductRecords(records, random);
    const seeded = [];
    const rest = [];
    records.forEach((record) => {
      if (seedRank.has(normalizedProductPath(record.path))) seeded.push(record);
      else rest.push(record);
    });
    seeded.sort((a, b) =>
      seedRank.get(normalizedProductPath(a.path)) -
      seedRank.get(normalizedProductPath(b.path))
    );
    return [...seeded, ...shuffledProductRecords(rest, random)];
  }

  function rankProductRecords(
    records,
    activeFilter = 'all',
    now = Date.now(),
    random = null
  ) {
    const sourceOrder = [...records].sort((a, b) =>
      Number(a.originalIndex || 0) - Number(b.originalIndex || 0)
    );
    const candyzLast = (items) => [...items].sort((a, b) => {
      const aAvailable = a.available !== false;
      const bAvailable = b.available !== false;
      if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
      const aCandyz = String(a.collection || '').toLowerCase().includes('candyz');
      const bCandyz = String(b.collection || '').toLowerCase().includes('candyz');
      if (aCandyz && !bCandyz) return 1;
      if (!aCandyz && bCandyz) return -1;
      return Number(a.originalIndex || 0) - Number(b.originalIndex || 0);
    });

    // Reported, not enforced. Staleness means "go re-pull the 30-day demand
    // numbers", not "stop pinning the top 3".
    lastSnapshotWasCurrent =
      Number.isFinite(Number(now)) &&
      Number(now) >= Date.parse(MERCHANDISING_POLICY.observedAt) &&
      Number(now) <= Date.parse(MERCHANDISING_POLICY.expiresAt);

    if (normalizeTag(activeFilter) !== 'all') {
      return candyzLast(sourceOrder).map((record) => record.id);
    }

    const discoveryRandom =
      typeof random === 'function' ? random : seededProductRandom();
    const byPath = new Map(
      sourceOrder.map((record) => [normalizedProductPath(record.path), record])
    );
    // The top 3 are ALWAYS pinned. An expired snapshot means the demand data is
    // stale and wants refreshing — it does not mean the fold should suddenly go
    // random, which is what emptying this array used to do. Staleness is reported
    // on window.__patchistryMerchandising.snapshotIsCurrent instead.
    const anchors = MERCHANDISING_POLICY.winnerPaths
      .map((path) => byPath.get(path))
      .filter((record) => record && record.available !== false);
    const anchorIds = new Set(anchors.map((record) => record.id));
    const available = sourceOrder.filter((record) =>
      !anchorIds.has(record.id) && record.available !== false
    );
    const unavailable = sourceOrder.filter((record) =>
      !anchorIds.has(record.id) && record.available === false
    );
    const newDrop = [];
    const candyz = [];
    const storytime = [];
    const general = [];

    available.forEach((record) => {
      // Order matters. Storytime is tested FIRST so a recent bryguy patch can't
      // ride the new-drop band straight past its own demotion. A brand-new
      // Candyz (Whoop Ass) counts as NEW rather than Candyz — recency is the
      // stronger signal while it holds, and it rejoins the Candyz pool the day
      // the window closes.
      if (recordIsStorytime(record)) {
        storytime.push(record);
      } else if (recordIsNewDrop(record, now)) {
        newDrop.push(record);
      } else if (recordIsCandyz(record)) {
        candyz.push(record);
      } else {
        general.push(record);
      }
    });

    const band = Math.max(0, Number(MERCHANDISING_POLICY.bandSize) || 0);
    const newBand = Math.max(0, Number(MERCHANDISING_POLICY.newDropBandSize) || band);
    const newOrdered = orderedNewDrop(newDrop, now, discoveryRandom);
    const candyzOrdered = seededFirst(
      candyz,
      MERCHANDISING_POLICY.candyzSeedPaths,
      discoveryRandom
    );

    // Band overflow is NOT buried. It falls into the same single shuffle as
    // everything else, so a Candyz sitting at position 7 this visit can be at
    // position 4 on the next one. That is the rediscovery half of the contract,
    // and it replaces the old lane where every Candyz ranked dead last.
    const tail = shuffledProductRecords(
      [
        ...newOrdered.slice(newBand),
        ...candyzOrdered.slice(band),
        ...general
      ],
      discoveryRandom
    );

    return [
      ...anchors,
      ...newOrdered.slice(0, newBand),
      ...candyzOrdered.slice(0, band),
      ...tail,
      ...shuffledProductRecords(storytime, discoveryRandom),
      ...unavailable
    ].map((record) => record.id);
  }

  window.__patchistryMerchandising = Object.freeze({
    snapshotIsCurrent: function () { return lastSnapshotWasCurrent; },
    observedAt: MERCHANDISING_POLICY.observedAt,
    expiresAt: MERCHANDISING_POLICY.expiresAt,
    rank: rankProductRecords
  });

  function normalizeTag(tag) {
    return String(tag || '').trim().toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  const FILTER_PROFILES = Object.freeze({
    'badge-reel': Object.freeze([
      'all', 'mom-life', 'pet-parents', 'coffee-shift-fuel', 'mood-chaos',
      'country-western', 'california-travel', 'spooky-season',
    ]),
    'build-yours': Object.freeze([
      // 2026-11-03 reorganised: occasions, people & hobbies, lines (research in audits)
      'all', 'bachelorette', 'funny', 'new-drops', 'moms', 'candyz',
      'game-day', 'fall', 'americana',
      'dads', 'pets', 'golf', 'pickleball', 'country', 'outdoors',
      'lamoomoo', 'founders',
    ]),
  });

  function filterMode(mode) {
    return mode === 'badge-reel' ? 'badge-reel' : 'build-yours';
  }

  function filterProfile(mode, labels = {}) {
    return FILTER_PROFILES[filterMode(mode)].map((key) => Object.freeze({
      key,
      label: String(labels[key] || ''),
    }));
  }

  function parseBuilderFilters(raw, mode) {
    let values = [];
    try {
      values = Array.isArray(raw) ? raw : JSON.parse(String(raw || '[]'));
    } catch (_) {}
    if (!Array.isArray(values)) return [];

    const allowed = new Set(FILTER_PROFILES[filterMode(mode)].filter((key) => key !== 'all'));
    return [...new Set(values
      .filter((value) => typeof value === 'string')
      .filter((key) => allowed.has(key)))];
  }

  window.__PatchistryBuilderFilters = Object.freeze({
    profile: filterProfile,
    parse: parseBuilderFilters,
    available(cards, mode, labels = {}) {
      const present = new Set(Array.from(cards || []).flatMap((card) => (
        parseBuilderFilters(card?.dataset?.builderFilters, mode)
      )));
      return filterProfile(mode, labels).filter(({ key, label }) => label && (key === 'all' || present.has(key)));
    },
    matches(card, key, mode) {
      const active = normalizeTag(key || 'all');
      return active === 'all' || parseBuilderFilters(card?.dataset?.builderFilters, mode).includes(active);
    },
  });

  function money(cents, currency = window.Shopify?.currency?.active) {
    if (window.PatchistryMoney?.format) {
      return window.PatchistryMoney.format(cents, {
        currency,
        locale: document.documentElement.lang || window.Shopify?.locale
      });
    }
    const amount = Number(cents || 0) / 100;
    const code = /^[A-Z]{3}$/.test(String(currency || '').toUpperCase())
      ? String(currency).toUpperCase()
      : '';
    if (!code) return amount.toFixed(2);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (_) {
      return `${code} ${amount.toFixed(2)}`;
    }
  }

  function ensureManifesto(root) {
    const copy = window.PatchistryBuilderMode.configFor(root);
    let manifesto = $('.pcr-manifesto', root);
    const canvas = $('.pcr-canvas', root);
    if (!manifesto) {
      manifesto = document.createElement('section');
      manifesto.className = 'pcr-manifesto';
      manifesto.innerHTML = `<h2>${copy.manifestoHeading}</h2><p>${copy.manifestoBody}</p>`;
    }
    if (canvas && manifesto.nextElementSibling !== canvas) canvas.insertAdjacentElement('beforebegin', manifesto);
    $$('.pcr-manifesto', root).forEach((el, i) => { if (i > 0) el.remove(); });
  }

  function ensurePatchIntro(root) {
    // Liquid template renders the intro from admin-editable section settings
    // (patch_eyebrow / patch_heading / patch_subheading). Only fall back to
    // hardcoded content if Liquid didn't render one for some reason.
    let intro = $('.pcr-hybrid-patch-intro', root);
    const controls = $('.pcr-controls', root);
    if (!controls) return;
    if (!intro) {
      intro = document.createElement('section');
      intro.className = 'pcr-hybrid-patch-intro';
      intro.innerHTML = '<p class="pcr-hybrid-patch-intro__eyebrow">Step 2</p><h2>Choose Your Patches</h2><p>Explore official Patchistry collections and discover pieces that match your mood, style, and expression.</p>';
      controls.insertAdjacentElement('beforebegin', intro);
    }
    // If intro already exists (Liquid rendered it), don't touch the content —
    // that's the admin-editable text Brian set in Customize.
  }

  function ensureCanvasLock(root) {
    const copy = window.PatchistryBuilderMode.configFor(root);
    const rail = $('[data-pcr-canvas-rail]', root);
    if (!rail) return null;
    let lock = $('.pcr-canvas-lock-v1', root);
    if (!lock) {
      lock = document.createElement('div');
      lock.className = 'pcr-canvas-lock-v1';
      rail.insertAdjacentElement('beforebegin', lock);
    }
    lock.innerHTML = `<span class="pcr-canvas-lock-v1__label">${copy.selectionLabel}</span><span class="pcr-canvas-lock-v1__value" data-pcr-canvas-lock-copy>None Yet</span>`;
    return lock;
  }

  // 2026-09-24 — the chips used to hotlink 13 Unsplash stock photos (a third-party
  // origin, fetched at 160px, showing no product). Each chip now shows the first
  // published patch in its own category, served from the Shopify CDN.
  const FILTER_IMAGES = {
    'all':         'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-liquor-where-she-likes-it.png?v=1785230269&width=320', // Liquor Where She Likes It
    'new-drops':   'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/santa-monica.png?v=1787887950&width=320', // Santa Monica
    'candyz':      'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-fire.png?v=1785230733&width=320', // Fire
    'lamoomoo':    'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-re-bull.png?v=1785230548&width=320', // Re-Bull
    'no-filter':   'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-liquor-where-she-likes-it.png?v=1785230269&width=320', // Liquor Where She Likes It
    'unhinged':    'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-f-caw-f.png?v=1785230348&width=320', // F-Caw-F
    'country':     'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-country-roads.png?v=1785230493&width=320', // Country Roads
    'moms':        'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-strong-as-a-mother.png?v=1785229971&width=320', // Strong as a Mother
    'outdoors':    'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-forever-chasing-sunsets.png?v=1785230404&width=320', // Forever Chasing Sunsets
    'pets':        'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-can-i-pet-that-dog.png?v=1785230794&width=320', // Can I Pet That Dog?
    'wild-card':   'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-f-you.png?v=1785230632&width=320', // F- You
    'pickleball':  'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-hole-y-balls.png?v=1785230352&width=320', // Hole-y Balls
    'golf':        'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-id-tap-that.png?v=1785230705&width=320', // I'd Tap That
    'bachelorette':'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/patch-ciao-bella.png?v=1785230293&width=320', // Ciao Bella
  };

  function buildFilters(root) {
    const controls = $('.pcr-controls', root);
    if (!controls || controls.dataset.experienceFilters === 'true') return;
    controls.dataset.experienceFilters = 'true';

    const mode = root.dataset.pcrBuilderMode === 'badge-reel' ? 'badge-reel' : 'build-yours';
    const labelsElement = root.querySelector('[data-pcr-filter-labels]');
    let labels = {};
    try {
      labels = JSON.parse(labelsElement?.textContent || '{}');
    } catch (_) {}
    const cards = $$('[data-pcr-card]', root);
    const available = window.__PatchistryBuilderFilters.available(cards, mode, labels);
    const all = window.__PatchistryBuilderFilters.profile(mode, labels).find(({ key }) => key === 'all');
    const filters = available.some(({ key }) => key === 'all') ? available : [all].filter(Boolean);
    controls.querySelectorAll('[data-pcr-filter], [data-pcr-filter-decorator]').forEach((element) => element.remove());

    const button = (label, filter) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'pcr-filter';
      el.dataset.pcrFilter = filter;
      const selected = filter === 'all';
      el.classList.toggle('is-active', selected);
      el.setAttribute('aria-pressed', String(selected));
      const strong = document.createElement('strong');
      if (mode === 'badge-reel') {
        const img = FILTER_IMAGES[filter];
        if (img) el.style.backgroundImage = `url('${img}')`;
        strong.style.cssText = 'display:block;font-size:12px;font-weight:900;letter-spacing:-0.01em;line-height:1.1;position:relative;z-index:1;text-align:center;';
      }
      strong.textContent = label;
      el.appendChild(strong);
      return el;
    };

    filters.forEach(({ key, label }) => controls.appendChild(button(label, key)));
  }

  function applyFilterState(root, key, emit = true) {
    const mode = root.dataset.pcrBuilderMode === 'badge-reel' ? 'badge-reel' : 'build-yours';
    const requested = normalizeTag(key || 'all');
    const approved = new Set(window.__PatchistryBuilderFilters.profile(mode).map(({ key: profileKey }) => profileKey));
    const active = approved.has(requested) ? requested : 'all';
    $$('[data-pcr-filter]', root).forEach((button) => {
      const selected = button.dataset.pcrFilter === active;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    $$('[data-pcr-card]', root).forEach((card) => {
      card.dataset.pcrFilterMatch = String(window.__PatchistryBuilderFilters.matches(card, active, mode));
    });
    if (emit) root.dispatchEvent(new CustomEvent('patchistry:filter-changed', { detail: { key: active } }));
  }

  // Right-size canvas-card background images. product.images[] are full-res
  // (3MB+ each) — Shopify CDN respects ?width=, Unsplash respects ?w=. Without
  // this, Build Yours loaded ~7.3MB of backgrounds on mobile before the user did
  // anything. Cuts each to ~150-200KB. (2026-06-25 perf fix.)
  function sizeCanvasBg(u) {
    if (!u || /^data:/.test(u)) return u;
    if (/[?&](width|w)=/.test(u)) return u; // already sized (e.g. card img) — leave it
    if (/images\.unsplash\.com/.test(u)) return u + (u.indexOf('?') > -1 ? '&' : '?') + 'w=1000&q=70';
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=1000';
  }

  async function hydrateCanvasBackground(card) {
    if (!card || card.dataset.experienceBgReady === 'true') return;
    card.dataset.experienceBgReady = 'true';
    const fallback = card.querySelector('img')?.currentSrc || card.querySelector('img')?.src;
    if (fallback) card.style.setProperty('--pcr-canvas-bg', `url("${sizeCanvasBg(fallback)}")`);
    const url = card.dataset.productUrl;
    if (!url) return;
    const productUrl = url.split('?')[0].replace(/\/$/, '') + '.js';
    try {
      let product = productCache.get(productUrl);
      if (!product) {
        const res = await fetch(productUrl, { headers: { Accept: 'application/json' }, cache: 'force-cache' });
        if (!res.ok) throw new Error('product fetch failed');
        product = await res.json();
        productCache.set(productUrl, product);
      }
      const images = Array.isArray(product.images) ? product.images : [];
      // Perf (2026-06-25): images[1] is a full-res lifestyle shot (~3MB each) and
      // the JS resize path proved unreliable on the live CDN. Use the already-
      // rendered + sized card image (fallback) as the background. The foreground
      // hat is the focus; the bg just needs subtle depth. Eliminates ~6MB.
      void images; // (images[1] lifestyle shots intentionally unused — see above)
      if (fallback) card.style.setProperty('--pcr-canvas-bg', `url("${sizeCanvasBg(fallback)}")`);
    } catch (_) {}
  }

  function ensureMenu() {
    document.querySelectorAll('.pcr-menu-button, .pcr-menu-panel').forEach((el) => el.remove());
  }

  async function syncCart() {
    const button = document.querySelector('.pcr-menu-button');
    const panel = document.querySelector('.pcr-menu-panel');
    if (!button && !panel) return;
    let cart = { item_count: 0, total_price: 0 };
    try {
      const res = await fetch(window.PatchistryBuilderMode.cartJsonUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (res.ok) cart = await res.json();
    } catch (_) {}
    const itemCount = Number(cart.item_count || 0);
    if (button) {
      button.setAttribute('data-patchistry-cart-count', String(itemCount));
      button.setAttribute('aria-label', itemCount ? `Open menu, ${itemCount} items in bag` : 'Open Patchistry menu');
    }
    if (panel) {
      const row = $('[data-patchistry-menu-cart]', panel);
      const count = $('[data-patchistry-menu-cart-count]', panel);
      const text = $('.pcr-menu-panel__cart-text', panel);
      if (count) count.textContent = String(itemCount);
      if (text) text.textContent = itemCount ? `Bag, ${money(cart.total_price, cart.currency)}` : 'Bag';
    }
  }

  function bindRoot(root) {
    if (root.dataset.experienceReady === 'true') return;
    root.dataset.experienceReady = 'true';
    root.addEventListener('click', (event) => {
      const filter = event.target.closest('[data-pcr-filter]');
      if (filter && root.contains(filter)) {
        event.preventDefault();
        event.stopPropagation();
        applyFilterState(root, filter.dataset.pcrFilter || 'all');
        // Only scroll if the filter bar is above the viewport (user has scrolled down past it)
        const filterBar = $('.pcr-controls', root);
        if (filterBar) {
          const barTop = filterBar.getBoundingClientRect().top;
          if (barTop < -20) {
            // Scroll to bring the filter bar back into view with 80px clearance
            const absTop = filterBar.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: Math.max(0, absTop), behavior: 'smooth' });
          }
        }
      }
    }, true);
  }

  function initRoot(root) {
    if (root.dataset.pcrFilterRuntimeReady === 'true') return;
    ensurePatchIntro(root);
    ensureCanvasLock(root);
    buildFilters(root);
    applyFilterState(root, 'all', false);
    $$('[data-pcr-canvas-card]', root).forEach(hydrateCanvasBackground);
    bindRoot(root);
    root.dataset.pcrFilterRuntimeReady = 'true';
    root.dispatchEvent(new CustomEvent('patchistry:filter-runtime-ready'));
  }

  function init() {
    ensureMenu();
    document.querySelectorAll(ROOT).forEach(initRoot);
    syncCart();
    setTimeout(syncCart, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:updated', syncCart);
})();
(() => {
  'use strict';

  /*
    Patchistry Builder Phase 1 bootstrap.
    Step 1 structure helpers live here.
    Active selection/cart logic lives in patchistry-builder-multihat-fix.js.
    Phase 2 is loaded directly by the Build Yours template loader.
  */

  const ROOT = '[id^="PatchistryCameraRoll-"]';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function ensureNoHat(root) {
    $$('[data-pcr-no-hat="true"]', root).forEach((card) => card.remove());
  }

  function ensureSummary(root) {
    const canvas = $('.pcr-canvas', root);
    if (!canvas || $('.pcr-canvas-step-summary', root)) return;

    const summary = document.createElement('div');
    const copy = window.PatchistryBuilderMode.configFor(root);
    summary.className = 'pcr-canvas-step-summary';
    summary.innerHTML = `<div class="pcr-canvas-step-summary__copy"><span class="pcr-canvas-step-summary__eyebrow">Step 1 Complete</span><strong class="pcr-canvas-step-summary__value" data-pcr-step1-value>${copy.stepSummaryInitial}</strong><span class="pcr-canvas-step-summary__sub" data-pcr-step1-sub>${copy.stepSummaryInitialSub}</span></div><button type="button" class="pcr-canvas-step-summary__change" data-pcr-change-canvas>Change</button>`;
    canvas.insertBefore(summary, canvas.firstChild);
  }

  function initRoot(root) {
    ensureNoHat(root);
    ensureSummary(root);
    root.classList.remove('pcr-step2-unlocked');
    root.classList.add('pcr-step2-locked');
  }

  function init() {
    $$(ROOT).forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
(() => {
  'use strict';

  const ROOT = '[id^="PatchistryCameraRoll-"]';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const isSelected = (el) => el && (el.classList.contains('is-selected') || el.getAttribute('aria-pressed') === 'true');
  const setSelected = (el, value) => {
    if (!el) return;
    el.classList.toggle('is-selected', value);
    el.setAttribute('aria-pressed', String(value));
  };
  const money = (cents, currency = window.Shopify?.currency?.active) => {
    if (window.PatchistryMoney?.format) {
      return window.PatchistryMoney.format(cents, {
        currency,
        locale: document.documentElement.lang || window.Shopify?.locale
      });
    }
    const amount = Number(cents || 0) / 100;
    const code = /^[A-Z]{3}$/.test(String(currency || '').toUpperCase())
      ? String(currency).toUpperCase()
      : '';
    if (!code) return amount.toFixed(2);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (_) {
      return `${code} ${amount.toFixed(2)}`;
    }
  };
  const cleanTitle = (title) => String(title || '').replace(/^Patchistry\s*/i, '').replace(/\s*Hat\s*/i, '').trim() || String(title || '').trim();

  function canvases(root) { return $$('[data-pcr-canvas-card]', root); }
  function noHat(root) { return $('[data-pcr-no-hat="true"]', root); }
  function selectedCanvases(root) { return canvases(root).filter(isSelected); }
  function selectedHats(root) { return selectedCanvases(root).filter((card) => card.dataset.pcrNoHat !== 'true'); }
  function selectedPatches(root) { return $$('[data-pcr-card].is-selected, [data-pcr-card][aria-pressed="true"]', root); }
  function patchOnly(root) { return root.dataset.pcrPatchOnly === '1' || root.classList.contains('pcr-patch-only-selected') || Boolean(noHat(root) && isSelected(noHat(root))); }
  function canvasImage(card) { return card?.dataset?.imageSrc || card?.querySelector('img')?.currentSrc || card?.querySelector('img')?.src || ''; }
  function step2Unlocked(root) { return root.classList.contains('pcr-step2-unlocked') || patchOnly(root); }
  function showCartReceiverError(root) {
    const error = $('[data-pcr-error]', root);
    if (!error) return;
    error.textContent = 'Cart connection unavailable. Please refresh and try again.';
    error.classList.add('is-on');
  }

  function requireCartReceiver(root) {
    const api = window.__patchistryCartSync;
    if (
      root.dataset.cartSyncReady === 'true' &&
      api &&
      typeof api.reconcile === 'function'
    ) return api;
    showCartReceiverError(root);
    return null;
  }

  function receiverLocksSelection(root, receiver, variantId = '') {
    return Boolean(receiver?.isSelectionLocked?.(root, String(variantId || '')));
  }

  function notifyCartSync(root, receiver = requireCartReceiver(root)) {
    if (!receiver) return false;
    receiver.reconcile(root);
    return true;
  }

  function scrollToPatches(root) {
    const target = $('.pcr-hybrid-patch-intro', root) || $('.pcr-controls', root);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 88;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function updateTrayCanvas(root, hats, isPatchOnly) {
    const copy = window.PatchistryBuilderMode.configFor(root);
    const img = $('[data-pcr-tray-canvas-img]', root);
    const fallback = $('[data-pcr-tray-canvas-nohat]', root);
    const name = $('[data-pcr-tray-canvas-name]', root);
    const first = hats[0];
    const src = first ? canvasImage(first) : '';

    if (img) {
      if (src) img.src = src;
      else img.removeAttribute('src');
      img.alt = first?.dataset?.title || '';
      img.classList.toggle('is-visible', Boolean(src));
    }
    if (fallback) fallback.style.display = src ? 'none' : 'flex';
    if (name) {
      if (isPatchOnly) name.textContent = copy.patchOnlyName;
      else if (!hats.length) name.textContent = copy.emptyName;
      else if (hats.length === 1) name.textContent = copy.selectedName(cleanTitle(first.dataset.title));
      else name.textContent = `${hats.length} ${copy.plural.toLowerCase()}`;
    }
  }

  function enforce(root) {
    const noHatCard = noHat(root);
    const selected = selectedCanvases(root);
    const noHatIsSelected = noHatCard && isSelected(noHatCard);

    if (noHatIsSelected) {
      canvases(root).forEach((card) => setSelected(card, card === noHatCard));
      return;
    }

    if (noHatCard) setSelected(noHatCard, false);
    if (window.PatchistryBuilderMode.configFor(root).mode === 'badge-reel' && selected.length > 1) {
      const firstSelected = selected[0];
      canvases(root).forEach((card) => setSelected(card, card === firstSelected));
    }
  }

  function summary(root, copy = window.PatchistryBuilderMode.configFor(root)) {
    if (patchOnly(root)) return copy.patchOnlyName;
    const hats = selectedHats(root);
    if (!hats.length) return copy.resetValue;
    return hats.map((card) => copy.selectedName(cleanTitle(card.dataset.title))).join(', ');
  }

  function sync(root) {
    const copy = window.PatchistryBuilderMode.configFor(root);
    enforce(root);

    const isPatchOnly = patchOnly(root);
    const hats = selectedHats(root);
    const patches = selectedPatches(root);
    const hasStep1 = isPatchOnly || hats.length > 0;
    const unlocked = copy.mode !== 'badge-reel' || (hasStep1 && step2Unlocked(root));

    if (!hasStep1) root.classList.remove('pcr-step2-unlocked');
    root.classList.toggle('pcr-step2-locked', !unlocked);
    root.classList.toggle('pcr-canvas-chosen', hasStep1);
    root.classList.toggle('pcr-no-hat-selected', isPatchOnly);
    if (!hasStep1) root.classList.remove('pcr-canvas-collapsed');

    // Persist on every sync so a refresh reliably re-opens step 2 (the click-path
    // save alone was proving flaky). Cleared when nothing is chosen. (2026-06-25)
    try {
      if (hasStep1) saveSessionCanvases(root);
      else sessionStorage.removeItem('patchistry_selected_canvases');
    } catch (_) {}

    const lock = $('[data-pcr-canvas-lock-copy]', root);
    if (lock) lock.textContent = hasStep1 ? summary(root, copy) : 'None Yet';

    const value = $('[data-pcr-step1-value]', root);
    if (value) value.textContent = hasStep1 ? summary(root, copy) : copy.resetValue;

    const sub = $('[data-pcr-step1-sub]', root);
    if (sub) {
      if (!hasStep1) sub.textContent = copy.stepEmptySub;
      else if (!unlocked) sub.textContent = copy.stepSelectedSub;
      else if (isPatchOnly) sub.textContent = copy.patchOnlySub;
      else sub.textContent = hats.length > 1 ? `${hats.length} ${copy.plural.toLowerCase()} ready. Build your signal.` : copy.readySub;
    }

    const tray = $('[data-pcr-tray]', root);
    const showTray = hasStep1 || patches.length > 0;
    root.classList.toggle('has-pcr-selection', showTray);
    if (tray) {
      tray.classList.toggle('is-visible', showTray);
      tray.setAttribute('aria-hidden', showTray ? 'false' : 'true');
    }

    // ── ONE WRITER ──────────────────────────────────────────────────────────
    // patchistry-tray-v5.js renders the tray from the CART, so the total it
    // shows is the discounted one Shopify will actually charge and the build
    // line says what is really in the bag. This function writes the same nodes
    // from the cards selected in the DOM: its total cannot know about the
    // 3/5/8 volume discount, and its build line uses the full product title
    // ("The Canvas - Black + 3 patches"). Whichever ran last won.
    //
    // Same guard the inline updateTray() already carries. Kept rather than
    // deleted because badge-reel does not load tray-v5 and still needs this.
    const traySyncOwnsTray = !!(window.__patchistryCartSync || window.__patchistryTraySyncLoaded);
    if (!traySyncOwnsTray) {
      const total = hats.reduce((sum, card) => sum + Number(card.dataset.priceCents || 0), 0) + patches.reduce((sum, card) => sum + Number(card.dataset.priceCents || 0), 0);
      const totalEl = $('[data-pcr-tray-total]', root);
      if (totalEl) totalEl.textContent = money(total);

      updateTrayCanvas(root, hats, isPatchOnly);

      const count = $('[data-pcr-tray-count]', root);
      if (count) {
        if (!hasStep1) count.textContent = copy.emptyInstruction;
        else if (isPatchOnly) count.textContent = patches.length ? `${copy.patchOnlyName}, ${patches.length} patch${patches.length === 1 ? '' : 'es'} selected` : copy.patchOnlyInstruction;
        else if (hats.length > 1) count.textContent = patches.length ? `${hats.length} ${copy.plural.toLowerCase()} + ${patches.length} patch${patches.length === 1 ? '' : 'es'}` : `${hats.length} ${copy.plural.toLowerCase()} selected`;
        else if (patches.length) count.textContent = copy.mode === 'badge-reel'
          ? `${copy.selectedName(cleanTitle(hats[0].dataset.title))} + ${patches.length} patch${patches.length === 1 ? '' : 'es'}`
          : `${cleanTitle(hats[0].dataset.title)} + ${patches.length} patch${patches.length === 1 ? '' : 'es'}`;
        else count.textContent = copy.selectedName(cleanTitle(hats[0].dataset.title));
      }
    }

    const add = $('[data-pcr-add]', root);
    if (add) {
      add.disabled = !hasStep1 || (isPatchOnly && !patches.length);
      if (!hasStep1) add.textContent = copy.chooseFirst;
      else if (isPatchOnly) add.textContent = patches.length ? copy.patchOnlyAction : copy.patchOnlyEmptyAction;
      else if (hats.length > 1) add.textContent = patches.length ? 'Add Builds To Cart' : 'Add Hats To Cart';
      else add.textContent = patches.length ? copy.buildAction : copy.baseOnlyAction;
    }

    updateUrlSelection(root);
  }

  function handleCanvasClick(root, card, event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const receiver = requireCartReceiver(root);
    if (!receiver) return;
    if (receiverLocksSelection(root, receiver, card.dataset.variantId)) {
      receiver.renderAll?.(root);
      return;
    }
    if (window.PatchistryBuilderMode.configFor(root).mode !== 'badge-reel' && card.dataset.pcrNoHat !== 'true' && receiver.chooseHat) {
      receiver.chooseHat(root, card);
      return;
    }
    root.classList.remove('pcr-patch-only-selected');

    const noHatCard = noHat(root);
    const wasSelected = isSelected(card);
    const isNoHat = card === noHatCard || card.dataset.pcrNoHat === 'true';
    const confirmsUrlIntent = wasSelected && Boolean(
      receiver.confirmQueryIntent?.(root, String(card.dataset.variantId || '')),
    );

    if (isNoHat) {
      canvases(root).forEach((item) => setSelected(item, item === card && (confirmsUrlIntent || !wasSelected)));
      if (!wasSelected) root.dataset.pcrPatchOnly = '1';
      else delete root.dataset.pcrPatchOnly;
    } else {
      if (noHatCard) setSelected(noHatCard, false);
      const config = window.PatchistryBuilderMode.configFor(root);
      if (config.mode === 'badge-reel') {
        canvases(root).forEach((item) => setSelected(item, item === card && (confirmsUrlIntent || !wasSelected)));
      } else {
        setSelected(card, confirmsUrlIntent || !wasSelected);
      }
      delete root.dataset.pcrPatchOnly;
    }

    // Instant unlock: any hat selected (or no-hat picked) opens the patch grid.
    // Multi-canvas still allowed — the canvas rail stays open, the user keeps tapping.
    // Deselect down to zero re-locks. Skip button (handled elsewhere) is the other unlock path.
    const anySelected = selectedCanvases(root).length > 0;
    root.classList.toggle('pcr-step2-unlocked', anySelected);

    sync(root);
    notifyCartSync(root, receiver);
    saveSessionCanvases(root);
  }

  function addToCartPayload(root) {
    const isPatchOnly = patchOnly(root);
    const items = [...(isPatchOnly ? [] : selectedHats(root)), ...selectedPatches(root)]
      .map((card) => Number(card.dataset.variantId))
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));
    return { items, isPatchOnly };
  }

  async function addToCart(root, event) {
    const { items, isPatchOnly } = addToCartPayload(root);
    if (!items.length) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const add = $('[data-pcr-add]', root);
    const original = add ? add.textContent : '';
    if (add) { add.disabled = true; add.textContent = 'Adding...'; }
    try {
      const res = await fetch(window.PatchistryBuilderMode.cartAddUrl(), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      if (!res.ok) throw new Error('add failed');
      if (add) add.textContent = 'Added ✓';
      document.dispatchEvent(new CustomEvent('cart:updated'));
      const copy = window.PatchistryBuilderMode.configFor(root);
      setTimeout(() => { if (add) { add.disabled = false; add.textContent = original || (isPatchOnly ? copy.patchOnlyAction : copy.buildAction); } sync(root); }, 850);
    } catch (_) {
      if (add) { add.disabled = false; add.textContent = original || 'Add To Cart'; }
    }
  }

  function bind(root) {
    if (root.dataset.multiHatFixReady === 'true') return;
    root.dataset.multiHatFixReady = 'true';

    root.addEventListener('click', (event) => {
      // Inspection has its own target listener; let it run before selection locks/dispatch.
      const inspection = event.target.closest('.pcr-inside-toggle');
      if (inspection && root.contains(inspection)) return;

      const rootReceiver = window.__patchistryCartSync;
      if (receiverLocksSelection(root, rootReceiver)) {
        const lockedControl = event.target.closest(
          '[data-pcr-canvas-skip], [data-pcr-canvas-next], [data-pcr-change-canvas], [data-pcr-canvas-card], [data-pcr-card], [data-pcr-add], [data-pcr-clear], [data-pcr-shake]',
        );
        if (lockedControl && root.contains(lockedControl)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
        rootReceiver.renderAll?.(root);
        return;
      }

      const skip = event.target.closest('[data-pcr-canvas-skip]');
      if (skip && root.contains(skip)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const receiver = requireCartReceiver(root);
        if (!receiver) return;
        if (receiverLocksSelection(root, receiver)) {
          receiver.renderAll?.(root);
          return;
        }
        canvases(root).forEach((card) => setSelected(card, false));
        root.classList.add('pcr-patch-only-selected', 'pcr-step2-unlocked', 'pcr-canvas-collapsed');
        // Sticky: "I have a hat" stays resolved even if all patches are later
        // deselected, so the cart-empty reset can't bounce them back to Step 1.
        root.dataset.pcrPatchOnly = '1';
        sync(root);
        notifyCartSync(root, receiver);
        setTimeout(() => scrollToPatches(root), 80);
        return;
      }

      const next = event.target.closest('[data-pcr-canvas-next]');
      if (next && root.contains(next)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const receiver = requireCartReceiver(root);
        if (!receiver) return;
        if (receiverLocksSelection(root, receiver)) {
          receiver.renderAll?.(root);
          return;
        }
        // Only collapse if at least one hat is selected; otherwise just scroll
        const hasSelection = selectedCanvases(root).length > 0;
        if (hasSelection) {
          root.classList.add('pcr-step2-unlocked', 'pcr-canvas-collapsed');
          root.classList.remove('pcr-patch-only-selected');
        } else {
          canvases(root).forEach((item) => {
            item.classList.add('is-flashing');
            setTimeout(() => item.classList.remove('is-flashing'), 700);
          });
        }
        sync(root);
        if (hasSelection) setTimeout(() => scrollToPatches(root), 80);
        return;
      }

      const change = event.target.closest('[data-pcr-change-canvas]');
      if (change && root.contains(change)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const receiver = requireCartReceiver(root);
        if (!receiver) return;
        if (receiverLocksSelection(root, receiver)) {
          receiver.renderAll?.(root);
          return;
        }
        root.classList.remove('pcr-patch-only-selected');
        root.classList.remove('pcr-step2-unlocked');
        root.classList.remove('pcr-canvas-collapsed');
        // User explicitly wants to re-pick their canvas — clear the sticky flag
        delete root.dataset.pcrPatchOnly;
        sync(root);
        const canvas = $('.pcr-canvas', root);
        if (canvas) canvas.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const card = event.target.closest('[data-pcr-canvas-card]');
      if (card && root.contains(card)) return handleCanvasClick(root, card, event);

      const patch = event.target.closest('[data-pcr-card]');
      if (patch && root.contains(patch)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const receiver = requireCartReceiver(root);
        if (!receiver) return;
        if (receiverLocksSelection(root, receiver, patch.dataset.variantId)) {
          receiver.renderAll?.(root);
          return;
        }
        if (root.classList.contains('pcr-step2-locked')) {
          patch.classList.remove('is-selected');
          patch.setAttribute('aria-pressed', 'false');
          canvases(root).forEach((item) => {
            item.classList.add('is-flashing');
            setTimeout(() => item.classList.remove('is-flashing'), 700);
          });
          return;
        }
        const wasOn = patch.classList.contains('is-selected') || patch.getAttribute('aria-pressed') === 'true';
        const confirmsUrlIntent = wasOn && Boolean(
          receiver.confirmQueryIntent?.(root, String(patch.dataset.variantId || '')),
        );
        const nextOn = confirmsUrlIntent || !wasOn;
        patch.classList.toggle('is-selected', nextOn);
        patch.setAttribute('aria-pressed', String(nextOn));
        sync(root);
        notifyCartSync(root, receiver);
        // Luxury micro-interaction (2026-06-25): subtle haptic "snap" + weighty
        // magnetic settle when PLACING a patch. AFTER sync() so the re-render can't
        // wipe the class. vibrate is Android-only (iOS no-ops). Reduced-motion via CSS.
        if (nextOn) {
          try { if (navigator.vibrate) navigator.vibrate(10); } catch (_) {}
          requestAnimationFrame(function () {
            patch.classList.remove('pcr-snap');
            void patch.offsetWidth;
            patch.classList.add('pcr-snap');
            setTimeout(function () { patch.classList.remove('pcr-snap'); }, 420);
          });
        }
        return;
      }

      const add = event.target.closest('[data-pcr-add]');
      if (add && root.contains(add)) return addToCart(root, event);

      // Note: Complete Purchase and Edit Cart are now handled natively by the <a> tags 
      // injected by patchistry-tray-v5.js. Since v5 live-syncs the cart on every click,
      // we do not need to do a bulk add-to-cart here. The browser will just navigate 
      // to the href.

      // Roll My Build — delegate to inline script's stored handler
      const shake = event.target.closest('[data-pcr-shake]');
      if (shake && root.contains(shake)) {
        if (typeof root.__patchistryShake === 'function') {
          event.stopPropagation();
          event.stopImmediatePropagation();
          root.__patchistryShake();
        }
        return;
      }

      setTimeout(() => sync(root), 0);
    }, true);

    const clear = $('[data-pcr-clear]', root);
    if (clear) {
      clear.addEventListener('click', (event) => {
        const receiver = requireCartReceiver(root);
        if (!receiver) return;
        if (receiverLocksSelection(root, receiver)) {
          event.preventDefault();
          event.stopPropagation();
          receiver.renderAll?.(root);
          return;
        }
        setTimeout(() => {
          canvases(root).forEach((card) => setSelected(card, false));
          root.classList.remove('pcr-patch-only-selected');
          root.classList.remove('pcr-step2-unlocked');
          root.classList.remove('pcr-canvas-collapsed');
          sync(root);
          notifyCartSync(root, receiver);
          saveSessionCanvases(root);
        }, 0);
      }, true);
    }
  }

  function saveSessionCanvases(root) {
    try {
      const selectedIds = selectedHats(root).map((card) => card.dataset.variantId).filter(Boolean);
      sessionStorage.setItem('patchistry_selected_canvases', JSON.stringify(selectedIds));
    } catch (_) {}
  }

  function restoreSessionCanvases(root) {
    try {
      const stored = sessionStorage.getItem('patchistry_selected_canvases');
      if (stored) {
        const ids = JSON.parse(stored);
        if (Array.isArray(ids) && ids.length > 0) {
          canvases(root).forEach((card) => {
            const id = card.dataset.variantId;
            if (id && ids.includes(id)) {
              setSelected(card, true);
            }
          });
          root.classList.add('pcr-step2-unlocked');
        }
      }
    } catch (_) {}
    enforce(root);
  }

  function reassertRestoredStep2(root) {
    // Cart restore is read-only, but tray-v5 can reflect cart-selected canvas cards
    // after builder sync has already run. If a canvas is visually selected during
    // init, re-open Step 2 by class only. Do not click cards or dispatch
    // patchistry:selection-changed; either can mutate the cart on refresh.
    if (selectedCanvases(root).length > 0 || patchOnly(root)) {
      sync(root);
      root.classList.add('pcr-step2-unlocked');
      root.classList.toggle('pcr-step2-locked', false);
    }
  }

  function updateUrlSelection(root) {
    try {
      const config = window.PatchistryBuilderMode.configFor(root);
      const baseKey = config.mode === 'badge-reel' ? 'reel' : 'canvas';
      const oppositeKey = config.mode === 'badge-reel' ? 'canvas' : 'reel';
      const isPatchOnly = patchOnly(root);
      const bases = selectedHats(root)
        .slice(0, config.mode === 'badge-reel' ? 1 : undefined)
        .map((card) => card.dataset.variantId)
        .filter(Boolean);
      const patches = selectedPatches(root)
        .map((card) => card.dataset.variantId)
        .filter(Boolean);

      if (isPatchOnly && config.mode !== 'badge-reel') {
        const noHatCard = noHat(root);
        if (noHatCard?.dataset?.variantId) bases.push(noHatCard.dataset.variantId);
      }

      const url = new URL(window.location);
      url.searchParams.delete(oppositeKey);
      if (bases.length) url.searchParams.set(baseKey, bases.join(','));
      else url.searchParams.delete(baseKey);
      if (patches.length) url.searchParams.set('patches', patches.join(','));
      else url.searchParams.delete('patches');
      window.history.replaceState(null, '', url.pathname + url.search);
    } catch (_) {}
  }

  function restoreUrlSelection(root) {
    try {
      const config = window.PatchistryBuilderMode.configFor(root);
      const selection = window.PatchistryBuilderMode.preselectionFor(
        window.location.search,
        root,
      );
      if (!selection.canvasIds.length && !selection.patchIds.length) return false;

      try { sessionStorage.removeItem('patchistry_selected_canvases'); } catch (_) {}
      canvases(root).forEach((card) => setSelected(card, false));
      $$('[data-pcr-card]', root).forEach((card) => setSelected(card, false));

      const canvasSet = new Set(selection.canvasIds);
      const patchSet = new Set(selection.patchIds);
      canvases(root).forEach((card) => {
        setSelected(card, canvasSet.has(String(card.dataset.variantId)));
      });
      $$('[data-pcr-card]', root).forEach((card) => {
        setSelected(card, patchSet.has(String(card.dataset.variantId)));
      });

      if (config.mode === 'badge-reel' && !canvasSet.size && patchSet.size) {
        root.dataset.pcrPatchOnly = '1';
        root.classList.add('pcr-patch-only-selected');
      } else {
        delete root.dataset.pcrPatchOnly;
        root.classList.remove('pcr-patch-only-selected');
      }
      return true;
    } catch (error) {
      console.error('restoreUrlSelection failed', error);
      return false;
    }
  }

  function init() {
    $$(ROOT).forEach((root) => {
      // Re-entry guard: bind + cart/session restore run ONCE per root. The old
      // code re-ran init() on every document click (see below), which re-fetched
      // /cart.js and overwrote the user's just-made selection — the "frozen/reset"
      // selection bug. The guard is per-element, so a genuinely new section
      // (theme editor reload) still initializes. Fixed 2026-06-25.
      if (root.__patchistryInitialized) return;
      root.__patchistryInitialized = true;
      
      const restoredFromUrl = restoreUrlSelection(root);
      const cartAuthority = root.dataset.cartSyncAuthority;
      const receiverOwnsRestore = cartAuthority === 'pending' || cartAuthority === 'ready';
      if (!restoredFromUrl && !receiverOwnsRestore && root.dataset.cartSyncReady !== 'true') {
        restoreSessionCanvases(root);
      }
      
      bind(root);
      sync(root);
      
      if (restoredFromUrl) {
        root.classList.add('pcr-step2-unlocked');
        root.classList.toggle('pcr-step2-locked', false);
        sync(root);
      }
      
      setTimeout(() => sync(root), 120);
      setTimeout(() => sync(root), 500);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  // Direct receiver edits (hat replacement / quantity) must update saved intent
  // only after Shopify confirms the cart and removed selections are cleared.
  document.addEventListener('patchistry:cart-confirmed', (event) => {
    const root = event.detail?.rootId ? document.getElementById(event.detail.rootId) : null;
    if (!root || !root.matches(ROOT)) return;
    sync(root);
    saveSessionCanvases(root);
  });

  // (Removed: document click → init loop. Selections are handled by the localized
  //  handler bound in bind(root); re-running init on every click overwrote them.)
  // URL restoration runs on initialization.
})();
